'use client'

import {
  type FC,
  type ReactNode,
  type SubmitEventHandler,
  useCallback,
  useContext,
  useState,
} from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'

import { type LoginInitiateResponseV1 } from '@redteclab/api/clients/composition-service-loyalty'

import { useHookFormFriendlyCaptchaFieldControls } from '../../../friendly-captcha/components/FriendlyCaptchaChallenge/useHookFormFriendlyCaptchaFieldControls'
import { useGlobalConfigContext } from '../../../global-config/context/globalConfigContext'
import { LoginRegistrationUiContext } from '../../../login-registration/context/LoginRegistrationUiContext'
import {
  LOGIN_OTP_STATE,
  LoginRegistrationUserLoginStateContext,
} from '../../../login-registration/context/LoginRegistrationUserLoginStateContext'
import { TENANT } from '../../../tenant/tenantTypes'
import { urlResolverGetForgotPassword } from '../../../url-handling/urlResolver'
import { useApiSwrLoginInitiateRequestMutation } from '../api/useApiSwrLoginInitiateRequestMutation'
import { type LoginEmailTracking } from '../loginEmailTracking'
import { useTwoStepLoginEmailTracking } from '../twoStepLoginEmailTracking'

import {
  LoginRegistrationUserLoginEmailForm,
  type LoginRegistrationUserLoginFormData,
} from './LoginRegistrationUserLoginEmailForm'

type LoginInitiateSubmitResult =
  'not-submitted' | 'submitted' | 'unsupported-next-page'

const resolveLoginNextPage = (
  loginNextPage: LoginInitiateResponseV1,
  setLoginOtpState: (loginOtpState: LOGIN_OTP_STATE) => void,
): LoginInitiateSubmitResult => {
  switch (loginNextPage.nextPage) {
    case 'OTP_LOGIN': {
      setLoginOtpState(LOGIN_OTP_STATE.OTP_INPUT)

      return 'submitted'
    }
    case 'PASSWORD_LOGIN': {
      setLoginOtpState(LOGIN_OTP_STATE.PASSWORD_INPUT)

      return 'submitted'
    }
    default: {
      return 'unsupported-next-page'
    }
  }
}

const submitLoginInitiateRequest = async ({
  formData,
  loginInitiateRequestMutation,
  setLoginOtpState,
  tracking,
}: {
  formData: LoginRegistrationUserLoginFormData
  loginInitiateRequestMutation: ReturnType<
    typeof useApiSwrLoginInitiateRequestMutation
  >
  setLoginOtpState: (loginOtpState: LOGIN_OTP_STATE) => void
  tracking: LoginEmailTracking
}): Promise<LoginInitiateSubmitResult> => {
  let loginNextPage: LoginInitiateResponseV1

  tracking.track('submit')

  try {
    loginNextPage = await loginInitiateRequestMutation.trigger({
      fcSolution: formData.fcSolution,
      userName: formData.username,
    })
  } catch {
    setLoginOtpState(LOGIN_OTP_STATE.LOGIN_REGISTER)
    tracking.track('failure')

    return 'not-submitted'
  }

  const submitResult = resolveLoginNextPage(loginNextPage, setLoginOtpState)

  if (submitResult === 'unsupported-next-page') {
    tracking.track('failure')
  }

  return submitResult
}

export const LoginRegistrationUserLoginFormEmailInput: FC<{
  children: ReactNode
}> = ({ children }) => {
  const globalConfig = useGlobalConfigContext()
  const { tenant } = globalConfig
  const { hideHeading } = useContext(LoginRegistrationUiContext)
  const { setLoginOtpState, setUsername, username } = useContext(
    LoginRegistrationUserLoginStateContext,
  )
  const [hasUnsupportedNextPageError, setHasUnsupportedNextPageError] =
    useState<boolean>(false)
  const [isSubmitInProgress, setIsSubmitInProgress] = useState(false)
  const tracking = useTwoStepLoginEmailTracking()
  const formMethods = useForm<LoginRegistrationUserLoginFormData>({
    defaultValues: {
      fcSolution: '',
      username,
    },
    mode: 'onBlur',
    reValidateMode: 'onChange',
  })
  const { handleSubmit, register, setValue, watch } = formMethods
  const { onCaptchaDone, watchFriendlyCaptchaChange } =
    useHookFormFriendlyCaptchaFieldControls({
      fieldName: 'fcSolution',
      register,
      setValue,
      watch,
    })
  const loginInitiateRequestMutation = useApiSwrLoginInitiateRequestMutation({
    onError: () => {
      setLoginOtpState(LOGIN_OTP_STATE.LOGIN_REGISTER)
    },
  })
  const onSubmit = useCallback(
    async (formData: LoginRegistrationUserLoginFormData): Promise<void> => {
      if (isSubmitInProgress || loginInitiateRequestMutation.isMutating) {
        return
      }

      setIsSubmitInProgress(true)
      setHasUnsupportedNextPageError(false)
      setUsername(formData.username)

      const submitResult = await submitLoginInitiateRequest({
        formData,
        loginInitiateRequestMutation,
        setLoginOtpState,
        tracking,
      })

      setHasUnsupportedNextPageError(submitResult === 'unsupported-next-page')
      if (submitResult !== 'submitted') {
        setIsSubmitInProgress(false)
      }
    },
    [
      isSubmitInProgress,
      loginInitiateRequestMutation,
      setLoginOtpState,
      setUsername,
      tracking,
    ],
  )
  const onFormSubmit: SubmitEventHandler<HTMLFormElement> = useCallback(
    (event): void => {
      void handleSubmit(onSubmit)(event)
    },
    [handleSubmit, onSubmit],
  )

  const isFriendlyCaptchaDone = Boolean(watchFriendlyCaptchaChange)

  return (
    <>
      {hideHeading ? null : (
        <h2 className="mb-6 text-l font-medium">
          {new Set<TENANT>([TENANT.BE, TENANT.CH]).has(tenant) ? (
            <FormattedMessage id="login.loginBox.headlineRebrand" />
          ) : (
            <FormattedMessage id="login.loginBox.headLine" />
          )}
        </h2>
      )}
      <FormProvider {...formMethods}>
        <LoginRegistrationUserLoginEmailForm
          forgotPasswordUrl={urlResolverGetForgotPassword(globalConfig)}
          hasUnsupportedNextPageError={hasUnsupportedNextPageError}
          isFriendlyCaptchaDone={isFriendlyCaptchaDone}
          loginInitiateRequestMutation={loginInitiateRequestMutation}
          onCaptchaDone={onCaptchaDone}
          onSubmit={onFormSubmit}
          tenant={tenant}
        >
          {children}
        </LoginRegistrationUserLoginEmailForm>
      </FormProvider>
    </>
  )
}
